import BackButton from 'components/backButton'
import { FormLayout, Spinner } from 'components/common'
import { Table, TableCell, TableRow } from 'components/common/minimalTable'
import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { Rma } from 'types/order'
import { fetchRmaDetails } from 'utils/api/orderApi'
import { PAGE_ADMIN_ORDERS, PAGE_NOT_FOUND } from 'utils/constants'

const RmaDetailsPage = () => {
  const [loading, setLoading] = useState(true)
  const [rmaDetails, setRmaDetails] = useState<Rma | null>(null)
  const { rmaId } = useParams()

  const navigate = useNavigate()

  const getRmaDetails = async (rmaId: string) => {
    try {
      setLoading(true)
      const { data } = await fetchRmaDetails(rmaId)
      setRmaDetails(data)
    } catch (error) {
      navigate(PAGE_NOT_FOUND.path)
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    navigate(`${PAGE_ADMIN_ORDERS.path}/${rmaDetails?.orderNo}`)
  }

  useEffect(() => {
    if (rmaId) {
      getRmaDetails(rmaId)
    }
  }, [])

  const totalPayment = useMemo(
    () => rmaDetails?.payments.reduce((sum, { amount }) => sum + amount, 0),
    [rmaDetails?.payments],
  )

  if (loading) {
    return <Spinner />
  }

  return (
    <>
      <Helmet>
        <title>ORMS RMA Details </title>
      </Helmet>

      <BackButton handleBack={handleBack} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="title">RMA Details</h3>
        <p className="rounded-lg bg-gray-800 px-3 py-1 text-sm font-semibold text-white shadow">
          {rmaDetails?.rmaNo}
        </p>
      </div>

      <FormLayout className="flex flex-col gap-1 !p-4 text-sm">
        <>
          <p>
            <span className="font-bold">Order NO: </span>
            {rmaDetails?.orderNo ?? 'NA'}
          </p>
          <p>
            <span className="font-bold">Created By: </span>
            {rmaDetails?.createdBy ?? 'N/A'}
          </p>

          <p>
            <span className="font-bold">RMA Type: </span>
            {rmaDetails?.isFullRMA ? 'Full RMA' : 'Partial RMA'}
          </p>
          <p>
            <span className="font-bold">Payments: </span>

            <span className="space-x-2 capitalize">
              {rmaDetails?.payments && rmaDetails?.payments?.length > 0 ? (
                <>
                  {rmaDetails.payments.map(({ amount, paymentType }, index) => (
                    <span key={index} className="">
                      {amount?.toFixed(2)}{' '}
                      <span className="text-gray-500">({paymentType})</span>
                    </span>
                  ))}
                  <span className="ml-2 font-semibold">
                    = {totalPayment?.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-red-500">Not Available</span>
              )}
            </span>
          </p>
        </>
      </FormLayout>

      <h3 className="title">Items to Return</h3>

      {rmaDetails?.products && rmaDetails?.products.length > 0 ? (
        <Table headers={['Barcode', 'Details', 'Return Quantity']}>
          {rmaDetails.products.map((product) => (
            <TableRow key={product._id}>
              <TableCell>
                <p className="mb-1 font-bold">{product?.description ?? '-'}</p>
                <p>
                  <strong>SKU:</strong> {product.barcode}
                </p>
                <p>
                  <strong>UOM:</strong> {product?.uom || 'NA'}
                </p>
              </TableCell>
              <TableCell>
                <p className="mb-1">
                  <strong>Reason: </strong> {product?.reason ?? '-'}
                </p>
                <p>
                  <strong>Condition:</strong> {product?.condition ?? '-'}
                </p>
              </TableCell>
              <TableCell>{product?.returnQty}</TableCell>
            </TableRow>
          ))}
        </Table>
      ) : (
        <p className="text-sm text-gray-600">No Product details available</p>
      )}
    </>
  )
}

export default RmaDetailsPage
